import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button, IconButton, Menu, MenuItem,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import GraphView from './GraphView';
import { useSaveDraftReport } from '../hooks/useDraftReport';
import { colors, typography, shadows, gradient } from '../theme/designTheme';

// ── Tokens ───────────────────────────────────────────────────────────────────
const FONT_SANS    = typography.fontFamily;
const FONT_DISPLAY = typography.fontFamilyDisplay;
const FONT_SERIF   = typography.fontFamilySerif;
const FONT_MONO    = typography.fontFamilyMono;

const VERDICT_BG = gradient.verdict;

const RISK_META = {
  CRITICAL: { label: 'Critical Priority', color: colors.critical, bg: colors.criticalBg, on: colors.onErrorContainer, score: 1.0 },
  HIGH:     { label: 'High Priority',     color: colors.critical, bg: colors.criticalBg, on: colors.onErrorContainer, score: 0.8 },
  MODERATE: { label: 'Moderate Priority', color: colors.warn,     bg: colors.warnBg,     on: colors.warn,              score: 0.5 },
  LOW:      { label: 'Low Priority',      color: colors.success,  bg: colors.successContainer, on: colors.success,     score: 0.2 },
};
const getRiskMeta = (lvl) => RISK_META[(lvl || 'MODERATE').toUpperCase()] || RISK_META.MODERATE;

const confidenceLabel = (c) => {
  const n = typeof c === 'number' ? c : 0;
  if (n >= 0.75) return 'HIGH';
  if (n >= 0.5)  return 'MEDIUM';
  return 'LOW';
};

// ── Inline SVG icons ─────────────────────────────────────────────────────────
const Svg = ({ d, size = 14, stroke = true, ...rest }) => (
  <Box component="svg" viewBox="0 0 24 24"
    sx={{ width: size, height: size, flexShrink: 0, strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2, ...rest.sx }}
    fill={stroke ? 'none' : 'currentColor'} stroke={stroke ? 'currentColor' : 'none'}>
    {typeof d === 'string' ? <path d={d} /> : d}
  </Box>
);
const IconSparkle   = (p) => <Svg stroke={false} d="M12 2l2.4 5.6L20 10l-5.6 2.4L12 18l-2.4-5.6L4 10l5.6-2.4z" {...p} />;
const IconClose     = (p) => <Svg d={<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>} {...p} />;
const IconDownload  = (p) => <Svg d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>} {...p} />;
const IconChevDown  = (p) => <Svg d={<polyline points="6 9 12 15 18 9" />} {...p} />;
const IconEdit      = (p) => <Svg d={<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>} {...p} />;
const IconRegen     = (p) => <Svg d={<><path d="M21 2 3 22"/><path d="M21 2v8h-8"/></>} {...p} />;
const IconShield    = (p) => <Svg d={<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></>} {...p} />;
const IconCheck     = (p) => <Svg d={<polyline points="20 6 9 17 4 12" />} {...p} />;
const IconWarning   = (p) => <Svg d={<><path d="M10.3 3.9 2 18a2 2 0 0 0 1.7 3h16.6A2 2 0 0 0 22 18L13.7 3.9a2 2 0 0 0-3.4 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>} {...p} />;

// ── Status model ─────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { key: 'drafted',        label: 'Drafted',        pip: colors.warn,           chipBg: colors.warnBg,           chipFg: colors.warn },
  { key: 'reviewed',       label: 'Reviewed',       pip: colors.success,        chipBg: colors.successContainer, chipFg: colors.success },
  { key: 'needs-revision', label: 'Needs Revision', pip: colors.tertiary,       chipBg: colors.errorContainer,   chipFg: colors.onErrorContainer },
  { key: 'pending',        label: 'Pending',        pip: colors.outlineVariant, chipBg: colors.canvas,           chipFg: colors.textSecondary },
];
const STATUS_BY_KEY = Object.fromEntries(STATUS_OPTIONS.map(s => [s.key, s]));

// ── Section templates ────────────────────────────────────────────────────────
// Shared across case types — both application_fraud (fraud_signals) and
// sanctions (sanctions_signals) cases read from this map.
const FRAUD_SECTION_TITLES = {
  subject:           'Subject & Facility',
  alert_source:      'Alert Source & Triage',
  fraud_signals:     'Fraud Signals',
  network_signals:   'Network Signals',
  sanctions_signals: 'Sanctions Signals',
  risk_multipliers:  'Risk Multipliers',
};
const FRAUD_SECTION_AGENTS = {
  subject:           'Agent-drafted',
  alert_source:      'Drafted by Alert Triage',
  fraud_signals:     'Drafted by Fraud Agent',
  network_signals:   'Drafted by Network Agent',
  sanctions_signals: 'Drafted by Sanctions Agent',
  risk_multipliers:  'Drafted by Risk Multiplier Agent',
};
const FRAUD_SECTION_EVIDENCE = {
  subject:           { tag: 'ASIC',        bg: colors.primaryContainer, fg: colors.onPrimaryContainer },
  alert_source:      { tag: 'ALERT',       bg: colors.warnBg,           fg: colors.warn },
  fraud_signals:     { tag: 'TRANSACTIONS',bg: colors.errorContainer,   fg: colors.onErrorContainer },
  network_signals:   { tag: 'NETWORK',     bg: colors.successContainer, fg: colors.success },
  sanctions_signals: { tag: 'SANCTIONS',   bg: colors.errorContainer,   fg: colors.onErrorContainer },
  risk_multipliers:  { tag: 'RISK',        bg: colors.purpleBg,         fg: colors.purple },
};

// ── Styles (shared) ──────────────────────────────────────────────────────────
const kickerSx = {
  display: 'inline-flex', alignItems: 'center', gap: 0.5,
  fontFamily: FONT_DISPLAY, fontSize: '10px', letterSpacing: '0.14em',
  textTransform: 'uppercase', fontWeight: 800,
  px: '10px', py: '5px', borderRadius: '5px',
};
const metaKeySx = {
  fontFamily: FONT_DISPLAY, fontSize: '9.5px', fontWeight: 700,
  letterSpacing: '0.14em', textTransform: 'uppercase',
  color: colors.textSecondary, mb: 0.5,
};
const metaValSx = {
  fontFamily: FONT_MONO, fontSize: '12.5px', color: colors.textPrimary,
  fontWeight: 500, letterSpacing: '0.02em',
};

// ── Title block (kickers + headline + lede + meta row) ───────────────────────
function TitleBlock({ caseNumber, caseType, riskLevel, headline, lede, metaRow }) {
  const risk = getRiskMeta(riskLevel);
  const caseTypeLabel = (caseType || 'application_fraud')
    .replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ ...kickerSx, bgcolor: colors.primaryContainer, color: colors.onPrimaryContainer }}>
          REF-{caseNumber}
        </Box>
        <Box sx={{ ...kickerSx, bgcolor: risk.bg, color: risk.on, gap: 0.75 }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%', bgcolor: risk.color,
            boxShadow: `0 0 0 3px ${risk.color}33`,
            animation: 'rd-pulse 1.6s ease-in-out infinite',
            '@keyframes rd-pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
          }} />
          {risk.label}
        </Box>
        <Box sx={{ ...kickerSx, bgcolor: colors.canvas, color: colors.textSecondary }}>
          {caseTypeLabel}
        </Box>
      </Box>

      <Typography sx={{
        fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '32px',
        lineHeight: 1.1, letterSpacing: '-0.025em', color: colors.textPrimary,
        m: 0, mb: 1.75,
      }}>
        {headline}
      </Typography>

      {lede && (
        <Typography sx={{
          fontFamily: FONT_SERIF, fontSize: '15.5px', lineHeight: 1.6,
          color: colors.textPrimary, mb: 2.25, maxWidth: 700,
          '& mark': {
            background: 'rgba(0,90,196,0.10)', color: colors.textPrimary,
            padding: '0 3px', borderRadius: '3px', fontWeight: 500,
          },
          '& strong': { fontWeight: 600 },
        }}>
          <span dangerouslySetInnerHTML={{ __html: lede }} />
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 4, pt: 1.75, borderTop: `1px solid ${colors.canvas}` }}>
        {metaRow.map(({ k, v }) => (
          <Box key={k}>
            <Box sx={metaKeySx}>{k}</Box>
            <Box sx={metaValSx}>{v}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Verdict hero (dark red, with confidence ring) ────────────────────────────
function VerdictHero({ headline, body, pills, confidence, agentsCount }) {
  const pct = Math.max(0, Math.min(1, confidence ?? 0));
  const R = 26;
  const C = 2 * Math.PI * R;
  const offset = C * (1 - pct);

  return (
    <Box sx={{
      position: 'relative', overflow: 'hidden',
      background: VERDICT_BG, color: '#fff',
      borderRadius: '14px', p: '22px 24px', my: 3.5,
      boxShadow: '0 2px 6px rgba(61,14,17,0.3), 0 18px 36px rgba(61,14,17,0.18)',
    }}>
      <Box sx={{
        position: 'absolute', top: '-30%', right: '-15%',
        width: 360, height: 360, pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(255,140,140,0.22), transparent 65%)',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '-40%', left: '-10%',
        width: 280, height: 280, pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(200,80,80,0.15), transparent 70%)',
      }} />

      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.75 }}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75,
          fontFamily: FONT_DISPLAY, fontSize: '10.5px', letterSpacing: '0.14em',
          textTransform: 'uppercase', fontWeight: 800,
          bgcolor: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.15)',
          px: '10px', py: '5px', borderRadius: '6px',
        }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%', bgcolor: '#ff6b6b',
            boxShadow: '0 0 8px rgba(255,107,107,0.8)',
            animation: 'rd-pulse 1.6s ease-in-out infinite',
            '@keyframes rd-pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
          }} />
          Recommendation · Action Required
        </Box>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 1,
          fontFamily: FONT_MONO, fontSize: '10.5px', letterSpacing: '0.04em',
          color: 'rgba(255,255,255,0.6)',
        }}>
          <span>AGGREGATED FROM {agentsCount} AGENTS</span>
          <Box component="span" sx={{ opacity: 0.5 }}>·</Box>
          <span>JUST NOW</span>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr 90px', gap: 3, alignItems: 'center' }}>
        <Box>
          <Typography sx={{
            fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '20px',
            lineHeight: 1.25, letterSpacing: '-0.018em', mb: 1,
          }}>
            {headline}
          </Typography>
          {body && (
            <Typography sx={{
              fontFamily: FONT_SERIF, fontSize: '14px', lineHeight: 1.55,
              color: 'rgba(255,255,255,0.82)', fontWeight: 400,
              '& strong': { color: '#fff', fontWeight: 600 },
              whiteSpace: 'pre-wrap',
            }}>
              <span dangerouslySetInnerHTML={{ __html: body }} />
            </Typography>
          )}
          {pills && pills.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.75 }}>
              {pills.map((pill, i) => (
                <Box key={i} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  fontFamily: FONT_DISPLAY, fontSize: '10.5px', fontWeight: 600,
                  color: i === 0 ? '#ffd9d9' : 'rgba(255,255,255,0.85)',
                  bgcolor: i === 0 ? 'rgba(220,80,80,0.2)' : 'rgba(255,255,255,0.08)',
                  border: `1px solid ${i === 0 ? 'rgba(255,140,140,0.3)' : 'rgba(255,255,255,0.14)'}`,
                  px: 1.1, py: 0.5, borderRadius: '5px', letterSpacing: '0.02em',
                }}>
                  {pill}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
          pl: 2.75, borderLeft: '1px solid rgba(255,255,255,0.15)',
        }}>
          <Box sx={{ position: 'relative', width: 64, height: 64 }}>
            <Box component="svg" viewBox="0 0 64 64"
              sx={{ width: 64, height: 64, transform: 'rotate(-90deg)' }}>
              <circle cx="32" cy="32" r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="5" />
              <circle cx="32" cy="32" r={R} fill="none" stroke="#ff8a8a" strokeWidth="5"
                strokeLinecap="round" strokeDasharray={C} strokeDashoffset={offset} />
            </Box>
            <Box sx={{
              position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
              fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '13px', letterSpacing: '0.02em',
            }}>
              {confidenceLabel(pct)}
            </Box>
          </Box>
          <Box sx={{
            fontFamily: FONT_DISPLAY, fontSize: '9px', letterSpacing: '0.14em',
            textTransform: 'uppercase', fontWeight: 700, color: 'rgba(255,255,255,0.6)',
          }}>
            Confidence
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ── Status chip + popover menu ───────────────────────────────────────────────
function StatusChip({ status, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const info = STATUS_BY_KEY[status] || STATUS_BY_KEY.drafted;
  const open = Boolean(anchor);

  return (
    <>
      <Box
        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.625,
          fontFamily: FONT_DISPLAY, fontSize: '10px', fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          bgcolor: info.chipBg, color: info.chipFg,
          px: 1, py: '4px', borderRadius: '5px', cursor: 'pointer',
          userSelect: 'none',
          transition: 'filter 0.15s',
          '&:hover': { filter: 'brightness(0.98)' },
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
        <span>{info.label}</span>
        <IconChevDown size={8} sx={{ opacity: 0.55, ml: 0.25 }} />
      </Box>
      <Menu
        open={open} anchorEl={anchor} onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75, minWidth: 210, borderRadius: '10px', p: 0.75,
              bgcolor: colors.surface,
              border: `1px solid ${colors.canvas}`,
              boxShadow: '0 2px 6px rgba(43,52,56,0.08), 0 16px 36px rgba(43,52,56,0.14)',
            },
          },
        }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        {STATUS_OPTIONS.map((opt, i) => {
          const active = opt.key === status;
          const isDivider = i === 2; // divider before Needs Revision
          return (
            <React.Fragment key={opt.key}>
              {isDivider && <Box sx={{ height: '1px', bgcolor: colors.canvas, mx: 0.25, my: 0.5 }} />}
              <MenuItem
                onClick={() => { onChange(opt.key); setAnchor(null); }}
                sx={{
                  display: 'grid', gridTemplateColumns: '12px 1fr 14px', gap: 1.25,
                  alignItems: 'center', px: 1, py: 1, borderRadius: '7px',
                  fontFamily: FONT_SANS, fontSize: '12.5px', fontWeight: 500,
                  color: colors.textPrimary, letterSpacing: '-0.002em',
                  '&:hover': { bgcolor: colors.canvas },
                }}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: opt.pip }} />
                <span>{opt.label}</span>
                <IconCheck size={14} sx={{ color: colors.primary, opacity: active ? 1 : 0, strokeWidth: 3 }} />
              </MenuItem>
            </React.Fragment>
          );
        })}
      </Menu>
    </>
  );
}

// ── Numbered collapsible section ─────────────────────────────────────────────
function NumberedSection({ num, title, status, onStatusChange, open, onToggle, agentLabel, children }) {
  return (
    <Box component="article" sx={{
      borderTop: `1px solid ${colors.canvas}`, py: 2.25, px: 0.25,
    }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'grid', gridTemplateColumns: '26px 1fr auto auto',
          gap: 1.75, alignItems: 'center', cursor: 'pointer', userSelect: 'none', py: 0.5,
        }}
      >
        <Box sx={{
          width: 26, height: 26, borderRadius: '50%',
          display: 'grid', placeItems: 'center',
          fontFamily: FONT_MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.04em',
          bgcolor: open ? colors.primaryContainer : colors.canvas,
          color: open ? colors.onPrimaryContainer : colors.textSecondary,
          transition: 'background 0.15s, color 0.15s',
        }}>
          {num}
        </Box>
        <Typography sx={{
          fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '15px',
          letterSpacing: '-0.005em', color: colors.textPrimary,
        }}>
          {title}
        </Typography>
        <StatusChip status={status} onChange={onStatusChange} />
        <Box sx={{
          color: open ? colors.primary : colors.outlineVariant,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s',
          display: 'grid', placeItems: 'center',
        }}>
          <IconChevDown size={15} />
        </Box>
      </Box>

      {open && (
        <Box sx={{ mt: 1.5, pl: 5 }}>
          {children}
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            mt: 1.75, pt: 1.25, borderTop: `1px dashed ${colors.surfaceLow}`,
            fontSize: '11.5px', color: colors.textSecondary,
          }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <IconSparkle size={11} sx={{
                color: colors.primary,
                animation: 'rd-sparkle 2s ease-in-out infinite',
                '@keyframes rd-sparkle': {
                  '0%,100%': { opacity: 0.7, transform: 'scale(1)' },
                  '50%':     { opacity: 1,   transform: 'scale(1.15)' },
                },
              }} />
              {agentLabel}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button disableRipple size="small"
                startIcon={<IconEdit size={11} />}
                sx={{
                  fontFamily: FONT_SANS, fontWeight: 600, fontSize: '11px',
                  color: colors.textSecondary, textTransform: 'none',
                  px: 1.25, py: 0.75, borderRadius: '6px',
                  '&:hover': { bgcolor: colors.canvas, color: colors.textPrimary },
                }}>Edit</Button>
              <Button disableRipple size="small"
                startIcon={<IconRegen size={11} />}
                sx={{
                  fontFamily: FONT_SANS, fontWeight: 600, fontSize: '11px',
                  color: colors.primary, textTransform: 'none',
                  px: 1.25, py: 0.75, borderRadius: '6px',
                  '&:hover': { bgcolor: colors.primaryContainer },
                }}>Regenerate</Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Prose renderer (preserves whitespace, supports simple markdown-ish marks)─
function Prose({ text }) {
  if (!text) return null;
  return (
    <Typography sx={{
      fontFamily: FONT_SERIF, fontSize: '14.5px', lineHeight: 1.65,
      color: colors.textPrimary, whiteSpace: 'pre-wrap',
      '& strong': { fontWeight: 600 },
      '& em': { fontStyle: 'italic', color: colors.textSecondary },
    }}>
      {text}
    </Typography>
  );
}

// ── Subject helpers ──────────────────────────────────────────────────────────
const fmtSubjectMoney = (n) => {
  if (n == null) return null;
  const num = Number(n);
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1)}M`;
  if (num >= 1_000)     return `$${(num / 1_000).toFixed(0)}K`;
  return `$${num.toLocaleString()}`;
};
const fmtSubjectDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
// Build the canonical subject fact list from case data (preferred) with an
// LLM-supplied subject object as a fallback for fields case data doesn't hold
// (e.g. director name).
function buildSubjectFacts(caseData, subject) {
  const facility = caseData?.facility_type
    ? [fmtSubjectMoney(caseData.facility_amount), caseData.facility_type].filter(Boolean).join(' ')
    : subject?.facility;
  return [
    { k: 'Entity',   v: caseData?.customer_name || caseData?.customer_id || subject?.legalName || '—' },
    { k: 'Facility', v: facility || '—' },
    { k: 'Applied',  v: fmtSubjectDate(caseData?.applied_date) || '—', mono: true },
    { k: 'Deadline', v: fmtSubjectDate(caseData?.settlement_date) || subject?.settlementDate || '—', mono: true },
    { k: 'Industry', v: caseData?.industry_segment || '—' },
  ];
}

// ── Subject fact grid ────────────────────────────────────────────────────────
function SubjectFactGrid({ caseData, subject }) {
  const facts = buildSubjectFacts(caseData, subject);
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '18px 28px', py: 1.75, mb: 1.5,
    }}>
      {facts.map(({ k, v, mono }) => (
        <Box key={k}>
          <Box sx={metaKeySx}>{k}</Box>
          <Typography sx={{
            fontFamily: mono ? FONT_MONO : FONT_DISPLAY,
            fontWeight: mono ? 500 : 700,
            fontSize: mono ? '12.5px' : '13.5px',
            color: colors.textPrimary,
            letterSpacing: mono ? '0.02em' : '-0.005em', lineHeight: 1.3,
          }}>
            {v}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Right rail (progress card + evidence list) ───────────────────────────────
function RightRail({ reviewedCount, totalCount, items, subtitle = 'SIGNAL CATEGORIES' }) {
  const pct = totalCount ? Math.round((reviewedCount / totalCount) * 100) : 0;
  return (
    <Box sx={{
      bgcolor: colors.canvas, overflowY: 'auto',
      p: '22px 22px 26px', display: 'flex', flexDirection: 'column', gap: 1.75,
      '&::-webkit-scrollbar': { width: 6 },
      '&::-webkit-scrollbar-thumb': { bgcolor: colors.surfaceLow, borderRadius: 3 },
    }}>
      <Box sx={{
        bgcolor: colors.surface, borderRadius: '12px', p: '14px 16px',
        boxShadow: shadows.ambient,
      }}>
        <Box sx={{
          fontFamily: FONT_DISPLAY, fontSize: '10px', fontWeight: 700,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: colors.textSecondary, mb: 1.25,
        }}>
          Review Progress
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <Typography sx={{
            fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '24px',
            letterSpacing: '-0.02em', color: colors.textPrimary,
          }}>
            {reviewedCount}
          </Typography>
          <Typography sx={{
            fontFamily: FONT_MONO, fontSize: '11px',
            color: colors.textSecondary, fontWeight: 500,
          }}>
            / {totalCount} SECTIONS
          </Typography>
        </Box>
        <Box sx={{ height: 4, bgcolor: colors.canvas, borderRadius: '2px', overflow: 'hidden' }}>
          <Box sx={{
            height: '100%', width: `${pct}%`,
            background: gradient.primary, borderRadius: '2px',
            transition: 'width 0.3s',
          }} />
        </Box>
      </Box>

      {items && items.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', px: 0.25, pt: 0.5 }}>
            <Typography sx={{
              fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '13.5px',
              letterSpacing: '-0.005em', color: colors.textPrimary,
            }}>
              Signals Reviewed
            </Typography>
            <Typography sx={{
              fontFamily: FONT_MONO, fontSize: '10px',
              color: colors.textSecondary, letterSpacing: '0.06em',
            }}>
              {items.length} {subtitle}
            </Typography>
          </Box>

          {items.map((it, idx) => (
            <Box key={it.id || idx} sx={{
              bgcolor: colors.surface, borderRadius: '12px', p: '12px 14px',
              boxShadow: shadows.ambient,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  fontFamily: FONT_MONO, fontSize: '9.5px', fontWeight: 700,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  bgcolor: it.tagBg, color: it.tagFg,
                  px: '7px', py: '3px', borderRadius: '4px',
                }}>
                  {it.tag}
                </Box>
                <Box sx={{
                  fontFamily: FONT_MONO, fontSize: '9.5px', fontWeight: 600,
                  color: colors.textSecondary,
                }}>
                  {it.cite}
                </Box>
              </Box>
              <Typography sx={{
                fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '12.5px',
                letterSpacing: '-0.005em', color: colors.textPrimary, mb: 0.25,
              }}>
                {it.title}
              </Typography>
              <Typography sx={{
                fontSize: '11px', color: colors.textSecondary, lineHeight: 1.4,
              }}>
                {it.sub}
              </Typography>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}

// ── Signal-row helpers (used for sanctions red-flag rows) ────────────────────
function SignalRow({ tone = 'crit', icon, label, source, value }) {
  const tones = {
    crit: { bg: colors.errorContainer,   fg: colors.tertiary },
    warn: { bg: colors.warnBg,           fg: colors.warn },
    info: { bg: colors.primaryContainer, fg: colors.primary },
  };
  const t = tones[tone] || tones.crit;
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 1.5,
      alignItems: 'center', py: 1.25, borderBottom: `1px dashed ${colors.surfaceLow}`,
      '&:last-child': { borderBottom: 0 },
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        display: 'grid', placeItems: 'center', bgcolor: t.bg, color: t.fg,
      }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontFamily: FONT_SANS, fontWeight: 600, fontSize: '13px', lineHeight: 1.3, color: colors.textPrimary }}>
          {label}
        </Typography>
        {source && (
          <Typography sx={{
            fontFamily: FONT_MONO, fontSize: '10.5px',
            color: colors.textSecondary, letterSpacing: '0.04em', mt: 0.25,
          }}>
            {source}
          </Typography>
        )}
      </Box>
      <Typography sx={{
        fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '13.5px',
        letterSpacing: '-0.01em', color: t.fg,
      }}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Scored-flag report view (application_fraud + sanctions) ─────────────────
// The structured report shape is the same for both case types; only the
// recommendation framing and case-type chip change.
function FraudReportView({ data, caseData, caseNumber, caseType, statuses, setStatus, expanded, toggleExpanded }) {
  const resolvedCaseType = data.case_type || caseType || 'application_fraud';
  const isSanctionsReport = resolvedCaseType === 'sanctions';
  const sections = useMemo(() => {
    const raw = (data.sections || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    return raw.filter(s => s.key !== 'recommendation');
  }, [data.sections]);

  const summarySection = useMemo(
    () => (data.sections || []).find(s => s.key === 'summary'),
    [data.sections],
  );
  const recSection = useMemo(
    () => (data.sections || []).find(s => s.key === 'recommendation'),
    [data.sections],
  );

  const lede = data.summary || summarySection?.content || '';
  const agentsCount = sections.filter(s =>
    ['fraud_signals', 'network_signals', 'sanctions_signals', 'risk_multipliers'].includes(s.key)).length || 3;

  const confidence = typeof data.confidence === 'number'
    ? data.confidence
    : getRiskMeta(data.riskLevel).score;

  const recommendationHeadline = isSanctionsReport
    ? 'File STR, offboard primary subject, and investigate linked network'
    : 'Decline application and escalate applicant and linked entities for review';
  const recommendationBody = recSection?.content || '';
  const pills = data.actions || (isSanctionsReport
    ? ['File STR', 'Offboard subject', 'Investigate network']
    : ['Decline application', 'Escalate → Financial Crime', 'File SMR']);
  const agentsLabel = isSanctionsReport
    ? 'Network · Sanctions · Multiplier'
    : 'Fraud · Network · Multiplier';

  // Sanctions cases don't use the loan-shaped "ASIC" tag on the subject
  // section — swap it for a jurisdiction/sanctions-screening tag. Uses the
  // same tonal colour as the existing fraud chip so rest of the UI stays
  // visually consistent.
  const evidenceTagOverrides = isSanctionsReport
    ? { subject: { tag: 'SUBJECT', bg: colors.primaryContainer, fg: colors.onPrimaryContainer } }
    : {};

  const evidenceItems = sections.map((s, i) => {
    const meta = evidenceTagOverrides[s.key]
      || FRAUD_SECTION_EVIDENCE[s.key]
      || { tag: 'SIGNAL', bg: colors.canvas, fg: colors.textSecondary };
    const firstLine = (s.content || '').split('\n').find(l => l.trim()) || '';
    const trimmed = firstLine.length > 48 ? firstLine.slice(0, 48) + '…' : firstLine;
    return {
      id: s.key,
      tag: meta.tag, tagBg: meta.bg, tagFg: meta.fg,
      cite: `E${i + 1}`,
      title: s.title || FRAUD_SECTION_TITLES[s.key] || s.key,
      sub: trimmed || '—',
    };
  });

  const reviewedCount = sections.filter(s => statuses[s.key] === 'reviewed').length;

  return (
    <>
      <Box component="article" sx={{
        overflowY: 'auto', p: '26px 36px 36px',
        borderRight: `1px solid ${colors.canvas}`, minWidth: 0,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: colors.surfaceLow, borderRadius: 3 },
      }}>
        <TitleBlock
          caseNumber={caseNumber}
          caseType={resolvedCaseType}
          riskLevel={data.riskLevel}
          headline="Case Report"
          lede={lede}
          metaRow={[
            { k: 'Generated',      v: new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC' },
            { k: 'Flags Reviewed', v: `${sections.length} signal categories` },
            { k: 'Agents',         v: agentsLabel },
            { k: 'Confidence',     v: confidenceLabel(confidence) },
          ]}
        />

        <VerdictHero
          headline={recommendationHeadline}
          body={recommendationBody}
          pills={pills}
          confidence={confidence}
          agentsCount={agentsCount}
        />

        {sections.map((s, idx) => {
          const n = String(idx + 1).padStart(2, '0');
          const key = s.key;
          return (
            <NumberedSection
              key={key}
              num={n}
              title={s.title || FRAUD_SECTION_TITLES[key] || key}
              status={statuses[key] || 'drafted'}
              onStatusChange={(next) => setStatus(key, next)}
              open={expanded[key] ?? idx < 2}
              onToggle={() => toggleExpanded(key, idx < 2)}
              agentLabel={FRAUD_SECTION_AGENTS[key] || 'Agent-drafted'}
            >
              {key === 'subject' && !isSanctionsReport && (
                <SubjectFactGrid caseData={caseData} subject={data.subject} />
              )}
              <Prose text={s.content} />
            </NumberedSection>
          );
        })}
      </Box>

      <RightRail
        reviewedCount={reviewedCount}
        totalCount={sections.length}
        items={evidenceItems}
      />
    </>
  );
}

// ── Sanctions report view (StructuredReport) ─────────────────────────────────
function SanctionsReportView({ data, caseNumber, graphNodes, graphEdges, primeSuspectId, statuses, setStatus, expanded, toggleExpanded }) {
  const buildSections = () => {
    const list = [];
    if (data.subject)
      list.push({ key: 'subject', title: 'Subject Profile', kind: 'subject' });
    if ((data.behavioralRisk || []).length)
      list.push({ key: 'behavioral', title: 'Behavioral Risk Analysis', kind: 'behavioral' });
    if ((data.redFlags || []).length)
      list.push({ key: 'redflags', title: 'Risk Identifier Matrix', kind: 'redflags' });
    if ((data.proximityLogs || []).length)
      list.push({ key: 'proximity', title: 'High-Risk Transaction Logs', kind: 'proximity' });
    if ((data.recommendations || []).length)
      list.push({ key: 'recommendations', title: 'Investigative Recommendations', kind: 'recommendations' });
    if (data.finalVerdict)
      list.push({ key: 'verdict', title: 'Final Verdict & Sign-off', kind: 'verdict' });
    return list;
  };
  const sections = useMemo(buildSections, [data]);

  const confidence = typeof data.confidence === 'number'
    ? data.confidence
    : getRiskMeta(data.riskLevel).score;

  const recommendationHeadline = data.assessment
    || (data.finalVerdict && `${getRiskMeta(data.riskLevel).label} — Regulatory Compliance`)
    || 'Regulatory Compliance Review';
  const recommendationBody = data.finalVerdict?.text || '';
  const pills = (data.recommendations || []).slice(0, 5).map(r => r.label || r.type);

  const evidenceItems = (data.redFlags || []).slice(0, 6).map((rf, i) => ({
    id: rf.id || `rf-${i}`,
    tag: 'RED FLAG',
    tagBg: colors.errorContainer, tagFg: colors.onErrorContainer,
    cite: rf.id || `E${i + 1}`,
    title: rf.title,
    sub: rf.impact ? `Impact: ${rf.impact}` : rf.description,
  }));

  const reviewedCount = sections.filter(s => statuses[s.key] === 'reviewed').length;

  return (
    <>
      <Box component="article" sx={{
        overflowY: 'auto', p: '26px 36px 36px',
        borderRight: `1px solid ${colors.canvas}`, minWidth: 0,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: colors.surfaceLow, borderRadius: 3 },
      }}>
        <TitleBlock
          caseNumber={caseNumber}
          caseType={data.case_type || 'sanctions'}
          riskLevel={data.riskLevel}
          headline="Regulatory Compliance Report"
          lede={data.summary}
          metaRow={[
            { k: 'Generated',    v: new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC' },
            { k: 'Investigator', v: data.investigator || 'SENTINEL_ALPHA_07' },
            { k: 'Risk Score',   v: confidence.toFixed(2) },
          ]}
        />

        <VerdictHero
          headline={recommendationHeadline}
          body={recommendationBody}
          pills={pills}
          confidence={confidence}
          agentsCount={sections.filter(s => s.kind !== 'subject' && s.kind !== 'verdict').length || 3}
        />

        {sections.map((s, idx) => {
          const n = String(idx + 1).padStart(2, '0');
          return (
            <NumberedSection
              key={s.key}
              num={n}
              title={s.title}
              status={statuses[s.key] || 'drafted'}
              onStatusChange={(next) => setStatus(s.key, next)}
              open={expanded[s.key] ?? idx < 2}
              onToggle={() => toggleExpanded(s.key, idx < 2)}
              agentLabel="Agent-drafted"
            >
              {s.kind === 'subject' && (
                <Box sx={{
                  display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '18px 28px', py: 1.75,
                }}>
                  {[
                    { k: 'Legal Name',     v: data.subject?.legalName },
                    { k: 'Primary Entity', v: data.subject?.primaryEntity },
                    { k: 'Risk Rating',    v: data.subject?.riskRating, highlight: true },
                    { k: 'Ownership',      v: data.subject?.ownership },
                  ].map(({ k, v, highlight }) => (
                    <Box key={k}>
                      <Box sx={metaKeySx}>{k}</Box>
                      <Typography sx={{
                        fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '14px',
                        letterSpacing: '-0.005em',
                        color: highlight ? colors.critical : colors.textPrimary,
                      }}>
                        {v || '—'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {s.kind === 'behavioral' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {(data.behavioralRisk || []).map((item, i) => (
                    <Box key={i} sx={{
                      bgcolor: colors.surface, borderRadius: '10px', p: '12px 16px',
                      borderLeft: `2px solid ${colors.primary}`, boxShadow: shadows.ambient,
                    }}>
                      <Box sx={{ ...metaKeySx, color: colors.primary, mb: 0.5 }}>{item.title}</Box>
                      <Typography sx={{
                        fontFamily: FONT_SERIF, fontSize: '13.5px', lineHeight: 1.65,
                        color: colors.textPrimary,
                      }}>
                        {item.detail}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {s.kind === 'redflags' && (
                <Box>
                  {(data.redFlags || []).map((rf, i) => (
                    <SignalRow
                      key={rf.id || i}
                      tone="crit"
                      icon={<IconWarning size={13} />}
                      label={rf.title}
                      source={`${rf.id || `RF${i + 1}`} · ${rf.description || ''}`}
                      value={rf.impact || 'HIGH'}
                    />
                  ))}
                </Box>
              )}

              {s.kind === 'proximity' && (
                <Box sx={{
                  bgcolor: colors.surface, borderRadius: '10px', overflow: 'hidden',
                  boxShadow: shadows.ambient,
                }}>
                  <Box sx={{
                    display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
                    p: '12px 16px', bgcolor: colors.canvas,
                  }}>
                    {['ENTITY', 'VOLUME', 'JURISDICTION', 'TYPE', 'STATUS'].map(h => (
                      <Typography key={h} sx={{
                        fontFamily: FONT_DISPLAY, fontSize: '9.5px', fontWeight: 700,
                        letterSpacing: '0.1em', color: colors.textSecondary,
                      }}>
                        {h}
                      </Typography>
                    ))}
                  </Box>
                  {(data.proximityLogs || []).map((row, i) => {
                    const isHigh = /high|escalat/i.test(row.riskStatus || '');
                    const isMod = /moderate|review/i.test(row.riskStatus || '');
                    const statusColor = isHigh ? colors.critical : isMod ? colors.warn : colors.success;
                    const statusLabel = isHigh ? 'ESCALATED' : isMod ? 'REVIEW' : (row.riskStatus || 'OK');
                    return (
                      <Box key={i} sx={{
                        display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
                        p: '12px 16px',
                        borderTop: `1px solid ${colors.canvas}`,
                        '&:hover': { bgcolor: colors.canvas },
                      }}>
                        <Box>
                          <Typography sx={{ fontFamily: FONT_MONO, fontSize: '11px', fontWeight: 600, color: colors.textPrimary }}>
                            {row.entityId}
                          </Typography>
                          <Typography sx={{ fontSize: '10px', color: colors.textSecondary, mt: 0.25 }}>
                            {row.entityName}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '13px', color: row.volumeLinked ? colors.primary : colors.textMuted, alignSelf: 'center' }}>
                          {row.volumeLinked ? `$${Number(row.volumeLinked).toLocaleString()}` : 'N/A'}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: colors.textSecondary, alignSelf: 'center' }}>
                          {row.jurisdiction}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_DISPLAY, fontSize: '10px', fontWeight: 700, color: colors.tertiary, alignSelf: 'center' }}>
                          {(row.activityType || 'RF1').slice(0, 3).toUpperCase()}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, alignSelf: 'center' }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusColor }} />
                          <Typography sx={{ fontFamily: FONT_DISPLAY, fontSize: '10px', fontWeight: 700, color: statusColor, textTransform: 'uppercase' }}>
                            {statusLabel}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {s.kind === 'recommendations' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                  {(data.recommendations || []).map((rec, i) => (
                    <Box key={i} sx={{
                      bgcolor: colors.surface, borderRadius: '10px', p: 2,
                      boxShadow: shadows.ambient,
                    }}>
                      <Typography sx={{
                        fontFamily: FONT_DISPLAY, fontSize: '10px', fontWeight: 700,
                        letterSpacing: '0.12em', textTransform: 'uppercase',
                        color: colors.primary, mb: 1,
                      }}>
                        {rec.label || rec.type}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', lineHeight: 1.55, color: colors.textSecondary }}>
                        {rec.detail}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {s.kind === 'verdict' && (
                <Box sx={{
                  bgcolor: colors.surface, borderRadius: '10px', p: 2.5,
                  borderLeft: `3px solid ${colors.primary}`, boxShadow: shadows.ambient,
                }}>
                  <Typography sx={{
                    fontFamily: FONT_SERIF, fontSize: '13.5px', lineHeight: 1.75,
                    color: colors.textPrimary, mb: 1.5,
                  }}>
                    {data.finalVerdict?.text}
                  </Typography>
                  <Box sx={{ ...metaKeySx, color: colors.primary, mt: 1 }}>Authorization Tier</Box>
                  <Typography sx={{
                    fontFamily: FONT_DISPLAY, fontSize: '13px', fontWeight: 700, color: colors.primary,
                  }}>
                    {data.finalVerdict?.authorizationTier || 'FEDERAL OVERSIGHT LEVEL 4'}
                  </Typography>
                </Box>
              )}
            </NumberedSection>
          );
        })}

        {(graphNodes?.length > 0) && (
          <Box sx={{
            mt: 3, bgcolor: colors.surface, borderRadius: '12px',
            overflow: 'hidden', height: 280, boxShadow: shadows.ambient,
          }}>
            <GraphView
              data={{ nodes: graphNodes, edges: graphEdges || [] }}
              primeSuspectId={primeSuspectId}
              onNodeClick={() => {}}
            />
          </Box>
        )}
      </Box>

      <RightRail
        reviewedCount={reviewedCount}
        totalCount={sections.length}
        items={evidenceItems}
        subtitle="FLAGS ATTACHED"
      />
    </>
  );
}

// ── Full-markdown view (light theme) ─────────────────────────────────────────
function MarkdownReport({ content }) {
  return (
    <Box sx={{
      overflowY: 'auto', p: '26px 36px 36px', gridColumn: '1 / -1',
      '&::-webkit-scrollbar': { width: 6 },
      '&::-webkit-scrollbar-thumb': { bgcolor: colors.surfaceLow, borderRadius: 3 },
    }}>
      <ReactMarkdown
        components={{
          h1: (p) => <Typography {...p} sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '28px', color: colors.textPrimary, mt: 3, mb: 1.5, letterSpacing: '-0.025em' }} />,
          h2: (p) => <Typography {...p} sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '18px', color: colors.textPrimary, mt: 3, mb: 1.25 }} />,
          h3: (p) => <Typography {...p} sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '14px', color: colors.primary, mt: 2, mb: 0.75 }} />,
          p:  (p) => <Typography {...p} sx={{ fontFamily: FONT_SERIF, fontSize: '14px', lineHeight: 1.7, color: colors.textPrimary, mb: 1.25 }} />,
          ul: (p) => <Box component="ul" {...p} sx={{ pl: 3, mb: 1.25 }} />,
          ol: (p) => <Box component="ol" {...p} sx={{ pl: 3, mb: 1.25 }} />,
          li: (p) => <Typography component="li" {...p} sx={{ fontFamily: FONT_SERIF, fontSize: '14px', lineHeight: 1.7, color: colors.textPrimary }} />,
          strong: (p) => <Typography component="span" {...p} sx={{ fontWeight: 600, color: colors.textPrimary }} />,
          em: (p) => <Typography component="span" {...p} sx={{ fontStyle: 'italic', color: colors.textSecondary }} />,
          hr: (p) => <Box {...p} sx={{ borderBottom: `1px solid ${colors.canvas}`, my: 2.5 }} />,
          pre: (p) => <Box component="pre" {...p} sx={{
            bgcolor: colors.canvas, p: 1.75, borderRadius: 1.5, overflowX: 'auto',
            fontFamily: FONT_MONO, fontSize: '12px', color: colors.textPrimary, mb: 1.5,
          }} />,
          code: ({ node, inline, ...p }) =>
            inline
              ? <Typography component="code" {...p} sx={{ bgcolor: colors.primaryContainer, color: colors.primary, px: 0.75, py: 0.2, borderRadius: 0.75, fontFamily: FONT_MONO, fontSize: '12px' }} />
              : <Typography component="code" {...p} sx={{ fontFamily: FONT_MONO, color: colors.textPrimary }} />,
          table: (p) => <Box component="table" {...p} sx={{ width: '100%', borderCollapse: 'collapse', mb: 2, fontFamily: FONT_SANS }} />,
          th: (p) => <Typography component="th" {...p} sx={{
            border: `1px solid ${colors.canvas}`, p: 1, bgcolor: colors.canvas,
            fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '10.5px', textAlign: 'left',
            color: colors.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase',
          }} />,
          td: (p) => <Typography component="td" {...p} sx={{
            border: `1px solid ${colors.canvas}`, p: 1.25,
            fontFamily: FONT_SANS, fontSize: '12px', color: colors.textPrimary,
          }} />,
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}

// ── Full-markdown builder (kept from original, used by "Full Report" tab) ────
function buildMarkdownFromStructured(data, caseNumber, caseData) {
  const lines = [];
  const generated = new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
  lines.push(`# Case Report`);
  lines.push(`**Case:** #${caseNumber}  **Risk Level:** ${data.riskLevel || 'N/A'}  **Generated:** ${generated}`);
  if (data.investigator) lines.push(`**Investigator:** ${data.investigator}`);
  lines.push('');
  if (data.summary) { lines.push(`## Summary`); lines.push(data.summary); lines.push(''); }
  if (data.recommendation) { lines.push(`## Recommendation`); lines.push(`> ${data.recommendation}`); lines.push(''); }
  if (data.assessment) { lines.push(`## Assessment`); lines.push(data.assessment); lines.push(''); }

  // Subject — rendered as a list so it displays cleanly without GFM tables.
  // Prefer canonical case data; fall back to LLM-provided fields (e.g. director).
  const subjectFacts = [];
  const entity = caseData?.customer_name || caseData?.customer_id || data.subject?.legalName;
  const entityId = caseData?.customer_id || data.subject?.entityId;
  if (entity) subjectFacts.push(['Legal Name', entityId && entity !== entityId ? `${entity} (${entityId})` : entity]);
  const facility = caseData?.facility_type
    ? [fmtSubjectMoney(caseData.facility_amount), caseData.facility_type].filter(Boolean).join(' ')
    : data.subject?.facility;
  if (facility)                             subjectFacts.push(['Facility', facility]);
  if (data.subject?.director)               subjectFacts.push(['Director', data.subject.director]);
  if (data.subject?.channel)                subjectFacts.push(['Channel', data.subject.channel]);
  const appliedStr  = fmtSubjectDate(caseData?.applied_date);
  const settlesStr  = fmtSubjectDate(caseData?.settlement_date) || data.subject?.settlementDate;
  if (appliedStr)                           subjectFacts.push(['Applied', appliedStr]);
  if (settlesStr)                           subjectFacts.push(['Deadline', settlesStr]);
  if (caseData?.industry_segment)           subjectFacts.push(['Industry', caseData.industry_segment]);
  if (caseData?.business_address)           subjectFacts.push(['Address', caseData.business_address]);
  if (subjectFacts.length) {
    lines.push(`## Subject`);
    subjectFacts.forEach(([k, v]) => lines.push(`- **${k}:** ${v}`));
    lines.push('');
  }

  (data.sections || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)).forEach(s => {
    // Skip summary + recommendation (rendered above) and subject (built from caseData).
    if (['recommendation', 'summary', 'subject'].includes(s.key)) return;
    lines.push(`## ${s.title || s.key}`);
    if (s.content) lines.push(s.content);
    lines.push('');
  });
  if (data.behavioralRisk?.length) {
    lines.push(`## Behavioral Risk Analysis`);
    data.behavioralRisk.forEach(item => {
      lines.push(`### ${item.title}`);
      lines.push(item.detail);
      lines.push('');
    });
  }
  if (data.redFlags?.length) {
    lines.push(`## Risk Identifier Matrix`);
    data.redFlags.forEach(rf => {
      lines.push(`### ${rf.id} — ${rf.title}`);
      if (rf.description) lines.push(rf.description);
      if (rf.impact) lines.push(`**Impact:** ${rf.impact}`);
      lines.push('');
    });
  }
  if (data.proximityLogs?.length) {
    lines.push(`## High-Risk Transaction Logs`);
    lines.push(`| Entity | Volume | Jurisdiction | Type | Risk |`);
    lines.push(`|---|---|---|---|---|`);
    data.proximityLogs.forEach(row => {
      const vol = row.volumeLinked ? `$${Number(row.volumeLinked).toLocaleString()}` : 'N/A';
      lines.push(`| ${row.entityId} (${row.entityName}) | ${vol} | ${row.jurisdiction} | ${row.activityType} | ${row.riskStatus} |`);
    });
    lines.push('');
  }
  if (data.recommendations?.length) {
    lines.push(`## Investigative Recommendations`);
    data.recommendations.forEach(rec => {
      lines.push(`### ${rec.label || rec.type}`);
      lines.push(rec.detail);
      lines.push('');
    });
  }
  if (data.finalVerdict) {
    lines.push(`## Final Verdict`);
    lines.push(data.finalVerdict.text);
    if (data.finalVerdict.authorizationTier) lines.push(`**Authorization Tier:** ${data.finalVerdict.authorizationTier}`);
  }
  return lines.join('\n');
}

// ── Word (.doc) download ─────────────────────────────────────────────────────
// Minimal markdown → HTML converter. Intentionally small — Word opens an HTML
// blob served as application/msword and renders the basic structure (headings,
// paragraphs, lists, blockquotes, bold/italic) as a normal document.
const _escapeHtml = (s) => s
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const _inlineMd = (s) => s
  .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
  .replace(/(^|[^*])\*([^*]+?)\*(?!\*)/g, '$1<i>$2</i>');

function markdownToDocHtml(md, title) {
  const lines = (md || '').split('\n');
  const out = [];
  let inList = false;
  const closeList = () => { if (inList) { out.push('</ul>'); inList = false; } };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (line.startsWith('# '))       { closeList(); out.push(`<h1>${_inlineMd(_escapeHtml(line.slice(2)))}</h1>`); }
    else if (line.startsWith('## ')) { closeList(); out.push(`<h2>${_inlineMd(_escapeHtml(line.slice(3)))}</h2>`); }
    else if (line.startsWith('### ')){ closeList(); out.push(`<h3>${_inlineMd(_escapeHtml(line.slice(4)))}</h3>`); }
    else if (line.startsWith('- '))  { if (!inList) { out.push('<ul>'); inList = true; } out.push(`<li>${_inlineMd(_escapeHtml(line.slice(2)))}</li>`); }
    else if (line.startsWith('> '))  { closeList(); out.push(`<blockquote>${_inlineMd(_escapeHtml(line.slice(2)))}</blockquote>`); }
    else if (line.trim() === '')     { closeList(); }
    else                             { closeList(); out.push(`<p>${_inlineMd(_escapeHtml(line))}</p>`); }
  }
  closeList();
  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${_escapeHtml(title)}</title>
<style>
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #2b3438; }
  h1 { font-size: 22pt; margin: 0 0 12pt; letter-spacing: -0.02em; }
  h2 { font-size: 15pt; margin: 18pt 0 6pt; }
  h3 { font-size: 12pt; margin: 12pt 0 4pt; color: #005ac4; }
  p  { margin: 0 0 8pt; line-height: 1.5; }
  ul { margin: 0 0 8pt 20pt; padding: 0; }
  li { margin-bottom: 4pt; line-height: 1.45; }
  blockquote { margin: 8pt 0; padding: 8pt 12pt; border-left: 3pt solid #005ac4; color: #3a4247; }
  b, strong { color: #1a1f22; }
</style></head><body>${out.join('\n')}</body></html>`;
}

function downloadMarkdownAsDoc(md, caseNumber) {
  const html = markdownToDocHtml(md, `Case Report — ${caseNumber}`);
  // Leading BOM helps Word pick up UTF-8 correctly.
  const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `case-${caseNumber}-report.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Main export ──────────────────────────────────────────────────────────────
export default function ReportDialog({
  open, onClose, onSave, caseType, reportContent, reportStructured, reportError,
  caseNumber, caseData, graphNodes, graphEdges, primeSuspectId,
}) {
  const [view, setView] = useState('structured');
  const [saveState, setSaveState] = useState('idle');
  const [statuses, setStatuses] = useState({});
  const [expanded, setExpanded] = useState({});
  const saveDraft = useSaveDraftReport(caseNumber);

  const hasStructured = !!reportStructured;

  const isFraudReport = useMemo(() => {
    if (caseType === 'application_fraud') return true;
    if (reportStructured?.case_type === 'application_fraud') return true;
    if (Array.isArray(reportStructured?.sections) && reportStructured.sections.length > 0) return true;
    return false;
  }, [caseType, reportStructured]);

  // Reset per-section state whenever a fresh report arrives.
  useEffect(() => {
    if (!reportStructured) { setStatuses({}); setExpanded({}); return; }
    const nextStatuses = {};
    const nextExpanded = {};
    if (isFraudReport) {
      const secs = (reportStructured.sections || [])
        .filter(s => s.key !== 'recommendation');
      secs.forEach((s, i) => {
        nextStatuses[s.key] = 'drafted';
        nextExpanded[s.key] = i < 2;
      });
    } else {
      ['subject', 'behavioral', 'redflags', 'proximity', 'recommendations', 'verdict']
        .forEach((k, i) => { nextStatuses[k] = 'drafted'; nextExpanded[k] = i < 2; });
    }
    setStatuses(nextStatuses);
    setExpanded(nextExpanded);
  }, [reportStructured, isFraudReport]);

  const setStatus = useCallback((key, next) => {
    setStatuses(prev => ({ ...prev, [key]: next }));
  }, []);
  const toggleExpanded = useCallback((key, defaultOpen) => {
    setExpanded(prev => {
      const cur = prev[key] ?? defaultOpen;
      return { ...prev, [key]: !cur };
    });
  }, []);

  const fullMarkdown = hasStructured
    ? buildMarkdownFromStructured(reportStructured, caseNumber, caseData)
    : reportContent;

  const handleSaveAsDraft = async () => {
    if (!reportStructured) return;
    setSaveState('saving');
    try {
      await saveDraft.mutateAsync({
        report_name: `Case ${caseNumber} — Draft Report`,
        risk_level: reportStructured.riskLevel,
        risk_level_short: reportStructured.riskLevelShort,
        summary: reportStructured.summary,
        sections: reportStructured.sections || [],
        structured_data: reportStructured,
      });
      setSaveState('saved');
    } catch (err) {
      console.error('Save draft failed:', err);
      setSaveState('error');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            background: `
              radial-gradient(circle at 30% 20%, rgba(0,90,196,0.08), transparent 55%),
              radial-gradient(circle at 80% 80%, rgba(91,70,184,0.06), transparent 50%),
              rgba(13,20,26,0.80)
            `,
            backdropFilter: 'blur(4px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          bgcolor: colors.surface,
          borderRadius: '18px',
          width: 'min(1120px, calc(100vw - 48px))',
          height: 'min(860px, calc(100vh - 48px))',
          maxWidth: 'none', m: 0,
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          overflow: 'hidden',
          boxShadow: '0 4px 8px rgba(0,0,0,0.12), 0 40px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.04)',
          fontFamily: FONT_SANS,
          color: colors.textPrimary,
        },
      }}
    >
      {/* TOP BAR */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        p: '14px 20px', bgcolor: colors.surface,
        borderBottom: `1px solid ${colors.canvas}`, flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            position: 'relative', width: 34, height: 34, borderRadius: '10px',
            background: gradient.primary, color: '#fff',
            display: 'grid', placeItems: 'center',
            boxShadow: '0 2px 10px rgba(0,90,196,0.25)',
            '&::after': {
              content: '""', position: 'absolute', inset: -3,
              borderRadius: '13px', border: '1.5px solid rgba(0,90,196,0.2)',
              animation: 'rd-halo 2.4s ease-in-out infinite',
            },
            '@keyframes rd-halo': {
              '0%,100%': { opacity: 0.6, transform: 'scale(1)' },
              '50%':     { opacity: 0,   transform: 'scale(1.2)' },
            },
          }}>
            <IconSparkle size={16} sx={{ color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ fontSize: '11.5px', color: colors.textSecondary, mb: 0.25, fontWeight: 500 }}>
              Cases <Box component="span" sx={{ opacity: 0.5 }}>/</Box>{' '}
              <Box component="span" sx={{ fontFamily: FONT_MONO, color: colors.primary, fontWeight: 600 }}>
                REF-{caseNumber}
              </Box>
            </Box>
            <Box sx={{
              fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '15.5px',
              letterSpacing: '-0.005em', lineHeight: 1.2,
              display: 'flex', alignItems: 'center', gap: 1.25,
            }}>
              Case Report
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75,
                fontSize: '11px', fontWeight: 500,
                color: colors.success, bgcolor: colors.successContainer,
                px: 1, py: '3px', borderRadius: '6px',
              }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors.success }} />
                Generated just now
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {hasStructured && !reportError && (
            <Box sx={{
              display: 'inline-flex', bgcolor: colors.canvas, p: '3px',
              borderRadius: '8px', gap: '2px',
            }}>
              {[
                { key: 'structured', label: 'Structured View' },
                { key: 'raw',        label: 'Full Report' },
              ].map(({ key, label }) => {
                const active = view === key;
                return (
                  <Box key={key} onClick={() => setView(key)} sx={{
                    fontFamily: FONT_SANS, fontWeight: 600, fontSize: '12px',
                    color: active ? colors.textPrimary : colors.textSecondary,
                    bgcolor: active ? colors.surface : 'transparent',
                    px: '14px', py: '7px', borderRadius: '6px', cursor: 'pointer',
                    boxShadow: active ? '0 1px 2px rgba(43,52,56,0.08)' : 'none',
                    letterSpacing: '-0.003em',
                    transition: 'background 0.15s, color 0.15s',
                  }}>
                    {label}
                  </Box>
                );
              })}
            </Box>
          )}
          <IconButton
            title="Download as Word"
            disabled={!fullMarkdown}
            onClick={() => fullMarkdown && downloadMarkdownAsDoc(fullMarkdown, caseNumber)}
            sx={{
              width: 34, height: 34, borderRadius: '8px', color: colors.textSecondary,
              '&:hover': { bgcolor: colors.canvas, color: colors.textPrimary },
              '&.Mui-disabled': { opacity: 0.4 },
            }}
          >
            <IconDownload size={15} />
          </IconButton>
          <IconButton title="Close" onClick={onClose} sx={{
            width: 34, height: 34, borderRadius: '8px', color: colors.textSecondary,
            '&:hover': { bgcolor: colors.canvas, color: colors.textPrimary },
          }}>
            <IconClose size={15} />
          </IconButton>
        </Box>
      </Box>

      {/* BODY */}
      <DialogContent sx={{
        p: 0, bgcolor: colors.surface, overflow: 'hidden', minHeight: 0,
        display: 'grid', gridTemplateColumns: '1fr 300px',
      }}>
        {reportError ? (
          <Box sx={{ p: 4, gridColumn: '1 / -1' }}>
            <Typography sx={{ color: colors.critical, fontFamily: FONT_SANS, fontSize: '13px' }}>
              {reportError}
            </Typography>
          </Box>
        ) : hasStructured && view === 'structured' ? (
          isFraudReport
            ? <FraudReportView
                data={reportStructured}
                caseData={caseData}
                caseNumber={caseNumber}
                caseType={caseType}
                statuses={statuses}
                setStatus={setStatus}
                expanded={expanded}
                toggleExpanded={toggleExpanded}
              />
            : <SanctionsReportView
                data={reportStructured}
                caseNumber={caseNumber}
                graphNodes={graphNodes}
                graphEdges={graphEdges}
                primeSuspectId={primeSuspectId}
                statuses={statuses}
                setStatus={setStatus}
                expanded={expanded}
                toggleExpanded={toggleExpanded}
              />
        ) : (
          <MarkdownReport content={fullMarkdown || ''} />
        )}
      </DialogContent>

      {/* FOOTER */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        p: '14px 22px', bgcolor: colors.surface,
        borderTop: `1px solid ${colors.canvas}`, flexShrink: 0,
      }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: '11.5px', color: colors.textSecondary }}>
          <IconShield size={12} sx={{ color: colors.success }} />
          Output logged to case audit trail
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={onClose}
            disableRipple
            sx={{
              fontFamily: FONT_SANS, fontWeight: 600, fontSize: '12.5px',
              color: colors.textSecondary, textTransform: 'none',
              px: 2, py: 1.1, borderRadius: '9px',
              '&:hover': { bgcolor: colors.canvas, color: colors.textPrimary },
            }}
          >
            Discard
          </Button>

          {!reportError && isFraudReport && hasStructured && (
            <Button
              onClick={handleSaveAsDraft}
              disabled={saveState === 'saving' || saveState === 'saved'}
              disableRipple
              sx={{
                fontFamily: FONT_SANS, fontWeight: 600, fontSize: '12.5px',
                bgcolor: colors.canvas, color: colors.textPrimary,
                textTransform: 'none', px: 2, py: 1.1, borderRadius: '9px',
                '&:hover': { bgcolor: colors.surfaceLow },
                '&.Mui-disabled': { color: colors.textMuted, bgcolor: colors.canvas },
              }}
            >
              {saveState === 'saving' ? 'Saving…'
                : saveState === 'saved' ? 'Saved as latest draft'
                : saveState === 'error' ? 'Save failed — retry'
                : 'Save as latest draft'}
            </Button>
          )}
          {!reportError && !isFraudReport && reportContent && onSave && (
            <Button
              onClick={onSave}
              disableRipple
              sx={{
                fontFamily: FONT_SANS, fontWeight: 600, fontSize: '12.5px',
                bgcolor: colors.canvas, color: colors.textPrimary,
                textTransform: 'none', px: 2, py: 1.1, borderRadius: '9px',
                '&:hover': { bgcolor: colors.surfaceLow },
              }}
            >
              Save Report
            </Button>
          )}

          <Button
            startIcon={<IconCheck size={13} />}
            disableRipple
            onClick={onClose}
            sx={{
              fontFamily: FONT_SANS, fontWeight: 600, fontSize: '12.5px',
              background: gradient.primary, color: '#fff',
              textTransform: 'none', px: 2, py: 1.1, borderRadius: '9px',
              boxShadow: '0 2px 10px rgba(0,90,196,0.25), inset 0 1px 0 rgba(255,255,255,0.15)',
              '&:hover': { background: gradient.primary, filter: 'brightness(1.05)' },
            }}
          >
            Finalize &amp; act
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
